export const getMobileOperatingSystem = (userAgent) => {
  // Windows Phone must come first because its UA also contains "Android"
  if (/windows phone/i.test(userAgent)) {
    return 'Windows Phone';
  }

  if (/android/i.test(userAgent)) {
    return 'Android';
  }

  if (/iPad/.test(userAgent) && !window.MSStream) {
    return 'iPad';
  }

  if (/iPhone/.test(userAgent) && !window.MSStream) {
    return 'iPhone';
  }

  if (/iPod/.test(userAgent) && !window.MSStream) {
    return 'iPod';
  }

  if (/CrOS/.test(userAgent) && !window.MSStream) {
    return 'Chromebook Computer';
  }

  if (/Macintosh/.test(userAgent)) {
    return 'Apple Computer';
  }

  if (/Windows NT/.test(userAgent)) {
    return 'Windows Computer';
  }

  //Default Response
  return 'Computer';
};

export const determineAdServer = (str) => {
  if (!str) {
    return 'Website';
  }

  const lowerStr = str.toLowerCase();

  const adTechDomains = [
    'amazon-adsystem',
    'smartadserver',
    'localhost',
    'googlesyndication',
    'doubleclick',
    'nexage',
    'adcolony',
    'sdk',
    'simpli.fi',
    'html-load.com',
    'html-load.cc',
    'safeframe',
    'onetag-sys.com',
    'fs-loader.com',
    'collector.dmp.cnna.io',
    'pubmatic.com',
    'rubiconproject.com',
    'appnexus.com',
    'adnxs.com',
    'openx.net',
    'casalemedia.com',
    'indexexchange.com',
    'sharethrough.com',
    'googleadservices.com',
    'googleapis.com',
    'googleads.com',
    'google.com',
    'smrtb.com',
    'emxdigital.com',
    'spotxchange.com',
    'spotx.tv',
  ];

  if (adTechDomains.some((domain) => lowerStr.includes(domain))) {
    return 'Ad Server';
  }

  if (
    lowerStr.includes('itunes') ||
    lowerStr.includes('play.google') ||
    lowerStr.includes('apps.apple.com')
  ) {
    return 'Application';
  }

  return 'Website';
};

export const extractBundleFromUrl = (url) => {
  if (!url) return null;
  const match = url.match(/[?&]bundle=([^&]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

export const extractHtmlLoadDomain = (url) => {
  if (!url) return null;
  try {
    const pathname = new URL(url).pathname;
    const segments = pathname.split('/').filter(Boolean);
    const domainSegment = segments.find(
      (s) => s.includes('.') && !s.includes(' ')
    );
    if (domainSegment) {
      return domainSegment.replace(/^www\./, '');
    }
  } catch (e) {
    const match = url.match(
      /html-load\.(?:com|cc)\/[^/]+\/([^/?#\s]+(?:\.[^/?#\s]+)+)/
    );
    if (match) {
      return match[1].replace(/^www\./, '');
    }
  }
  return null;
};

export const stripQueryString = (url) => {
  if (!url) return '';
  const queryIndex = url.indexOf('?');
  return queryIndex === -1 ? url : url.slice(0, queryIndex);
};

export const getDspFromImpression = (impression) => {
  if (!impression) return 'Unknown';
  const { IMPRESSION_APP_ID, APP_ID_IMPRESSION } = impression;
  // Primary: IMPRESSION_APP_ID (available on transactions; will be added to signups/sessions)
  if (IMPRESSION_APP_ID && IMPRESSION_APP_ID !== 'Unknown') {
    return IMPRESSION_APP_ID;
  }
  // Fallback: APP_ID_IMPRESSION (legacy field on signups/sessions)
  if (APP_ID_IMPRESSION && APP_ID_IMPRESSION !== 'Unknown') {
    return APP_ID_IMPRESSION;
  }
  return 'Unknown';
};

export const getReadableDomain = (impression, dsp) => {
  let inventoryType = 'Application';
  let impressionDomain = 'Unknown Domain';

  const {
    IMPRESSION_PAGE_URL,
    PAGE_URLHOST,
    IMPRESSION_REFERRER,
    IMPRESSION_APPNAME,
    IMPRESSION_APPSTORE_ID,
    IMPRESSION_SITE_ID,
    IMPRESSION_SITENAME,
    APP_ID_IMPRESSION,
    LINEITEMID,
  } = impression;

  let detectedDsp = dsp;
  if (!detectedDsp || detectedDsp === 'Unknown') {
    detectedDsp = getDspFromImpression(impression);
  }
  // Simplifi-specific fallback for domain resolution (html-load.com handling)
  if ((!detectedDsp || detectedDsp === 'Unknown') && LINEITEMID) {
    const lineItemStr = String(LINEITEMID);
    if (lineItemStr.toLowerCase().startsWith('simplifi')) {
      detectedDsp = 'Simplifi';
    }
  }
  if (detectedDsp) {
    detectedDsp = String(detectedDsp);
  }

  const isValid = (str) =>
    str &&
    str !== 'N/A' &&
    str !== '0' &&
    str !== '{APPNAME}' &&
    str !== 'Unknown Publisher' &&
    !str.includes('%%');

  const cleanStr = (str) =>
    decodeURIComponent(str).replace(/[+_]/g, ' ').split(':').pop();
  const isNumeric = (str) => /^\d+$/.test(str);

  let siteIdSuffix = '';
  if (isValid(IMPRESSION_SITE_ID) && isNumeric(IMPRESSION_SITE_ID)) {
    siteIdSuffix = ` (Site ID: ${cleanStr(IMPRESSION_SITE_ID)})`;
  } else if (isValid(IMPRESSION_SITENAME) && isNumeric(IMPRESSION_SITENAME)) {
    siteIdSuffix = ` (Site ID: ${cleanStr(IMPRESSION_SITENAME)})`;
  }

  const bundle = extractBundleFromUrl(IMPRESSION_PAGE_URL);

  // GAM-specific app detection
  if (
    detectedDsp &&
    detectedDsp.toLowerCase() === 'googleadsmanager' &&
    IMPRESSION_SITE_ID === 'adsenseformobileapps.com'
  ) {
    if (isValid(IMPRESSION_APPNAME)) {
      return {
        impressionDomain: `App: ${cleanStr(IMPRESSION_APPNAME)}`,
        inventoryType: 'Application',
      };
    }
  }

  // For HTML5 creatives, try to extract the real serving domain from the referrer
  if (isHtml5Creative(IMPRESSION_PAGE_URL) && isValid(IMPRESSION_REFERRER)) {
    try {
      const refUrl = new URL(IMPRESSION_REFERRER);
      const refHost = refUrl.hostname;
      if (refHost && determineAdServer(refHost) === 'Website') {
        return { impressionDomain: refHost, inventoryType: 'Website' };
      }
    } catch (e) {
      // Referrer isn't a valid URL, fall through to normal logic
    }
  }

  // Simplifi-specific resolution flow
  if (detectedDsp && detectedDsp.toLowerCase() === 'simplifi') {
    // Step 1: html-load.com / html-load.cc pattern
    const htmlLoadDomain = extractHtmlLoadDomain(IMPRESSION_PAGE_URL);
    if (htmlLoadDomain && !isHtmlFileName(htmlLoadDomain)) {
      return { impressionDomain: htmlLoadDomain, inventoryType: 'Website' };
    }

    // Step 2: Direct publisher URL in IMPRESSION_PAGE_URL
    if (isValid(IMPRESSION_PAGE_URL)) {
      try {
        const url = new URL(IMPRESSION_PAGE_URL);
        if (url.hostname && determineAdServer(url.hostname) === 'Website') {
          return { impressionDomain: url.hostname, inventoryType: 'Website' };
        }
      } catch (e) {
        // ignore
      }
    }

    // Step 3: Referrer hostname
    if (isValid(IMPRESSION_REFERRER)) {
      try {
        const refUrl = new URL(IMPRESSION_REFERRER);
        if (refUrl.hostname && determineAdServer(refUrl.hostname) === 'Website') {
          return { impressionDomain: refUrl.hostname, inventoryType: 'Website' };
        }
      } catch (e) {
        // ignore
      }
    }

    // Step 4: Fallback for unresolved numeric SITE_ID
    if (isValid(IMPRESSION_SITE_ID) && isNumeric(IMPRESSION_SITE_ID)) {
      return {
        impressionDomain: `In-App #${IMPRESSION_SITE_ID}`,
        inventoryType: 'Application',
      };
    }
  }

  if (isValid(IMPRESSION_SITENAME) && !isNumeric(IMPRESSION_SITENAME)) {
    impressionDomain = cleanStr(IMPRESSION_SITENAME);
    inventoryType =
      isValid(IMPRESSION_SITE_ID) || isValid(IMPRESSION_APPNAME)
        ? 'Website'
        : 'Application';
  } else if (
    isValid(PAGE_URLHOST) &&
    determineAdServer(PAGE_URLHOST) !== 'Ad Server'
  ) {
    impressionDomain = decodeURIComponent(PAGE_URLHOST) + siteIdSuffix;
    inventoryType = determineAdServer(PAGE_URLHOST);
  } else if (bundle) {
    impressionDomain = bundle + siteIdSuffix;
    inventoryType =
      determineAdServer(bundle) === 'Website'
        ? 'Application'
        : determineAdServer(bundle);
  } else if (isValid(IMPRESSION_APPNAME) && !isNumeric(IMPRESSION_APPNAME)) {
    impressionDomain = cleanStr(IMPRESSION_APPNAME);
    inventoryType = 'Application';
  } else if (isValid(IMPRESSION_SITE_ID) && !isNumeric(IMPRESSION_SITE_ID)) {
    impressionDomain = cleanStr(IMPRESSION_SITE_ID);
    inventoryType = 'Application';
  } else if (isValid(PAGE_URLHOST)) {
    impressionDomain = decodeURIComponent(PAGE_URLHOST) + siteIdSuffix;
    inventoryType = determineAdServer(PAGE_URLHOST);
  } else if (isValid(IMPRESSION_PAGE_URL)) {
    try {
      const url = new URL(IMPRESSION_PAGE_URL);
      impressionDomain =
        (url.hostname || IMPRESSION_PAGE_URL) + siteIdSuffix;
      inventoryType = determineAdServer(url.hostname);
    } catch (e) {
      impressionDomain = IMPRESSION_PAGE_URL + siteIdSuffix;
      inventoryType = 'Website';
    }
  } else if (siteIdSuffix) {
    // If we only have the numeric ID and no URL host
    impressionDomain = siteIdSuffix.replace(' (', '').replace(')', '');
    inventoryType = 'Application';
  }

  if (IMPRESSION_REFERRER && inventoryType !== 'Application') {
    const refServer = determineAdServer(IMPRESSION_REFERRER);
    if (refServer !== 'Ad Server') {
      inventoryType = refServer;
    }
  }

  // If a purely numeric ID was provided, it's almost certainly an in-app impression masked by the publisher
  if (siteIdSuffix && inventoryType !== 'Application') {
    inventoryType = 'Application';
  }

  // Final guard: reject anything that looks like an HTML creative file
  if (isHtmlFileName(impressionDomain)) {
    if (siteIdSuffix) {
      impressionDomain = siteIdSuffix.replace(' (', '').replace(')', '');
      inventoryType = 'Application';
    } else {
      impressionDomain = 'Unknown Domain';
      inventoryType = 'Application';
    }
  }

  return { impressionDomain, inventoryType };
};

export const getFaviconUrl = (domain, inventoryType) => {
  if (
    !domain ||
    domain === 'Unknown Domain' ||
    domain.includes('(Site ID:') ||
    domain.startsWith('In-App #') ||
    inventoryType !== 'Website'
  ) {
    return null;
  }

  // Ensure we just have the hostname
  let hostname = domain;
  try {
    if (hostname.startsWith('http')) {
      hostname = new URL(hostname).hostname;
    }
  } catch (e) {
    // If URL parsing fails, we'll try the string as-is
  }

  return `https://www.google.com/s2/favicons?domain=${hostname}&sz=64`;
};

export const getOutboundLink = (domain, inventoryType, os) => {
  if (!domain || domain === 'Unknown Domain' || domain.startsWith('In-App #')) return null;

  // Clean out the " (Site ID: xxxx)" suffix if it exists
  const cleanDomain = domain.split(' (Site ID:')[0].trim();

  // Don't try to link to purely numeric strings or known ad servers
  if (/^\d+$/.test(cleanDomain) || determineAdServer(cleanDomain) === 'Ad Server') {
    return null;
  }

  if (inventoryType === 'Application') {
    // If it looks like an Apple App Store ID (e.g. id123456789 or 123456789)
    if (
      os === 'iPhone' ||
      os === 'iPad' ||
      os === 'iPod' ||
      os === 'Apple Computer'
    ) {
      const idMatch = cleanDomain.match(/^(?:id)?(\d+)$/i);
      if (idMatch) {
        return `https://apps.apple.com/app/id${idMatch[1]}`;
      }
    }

    // Fallback to Google Play if it has a bundle ID format (e.g. com.weather.weather)
    if (cleanDomain.includes('.') && !cleanDomain.includes(' ')) {
      return `https://play.google.com/store/apps/details?id=${cleanDomain}`;
    }
  } else if (inventoryType === 'Website') {
    // Generate a standard website link
    if (!cleanDomain.startsWith('http')) {
      return `https://${cleanDomain}`;
    }
    return cleanDomain;
  }

  return null;
};

export const isMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'number') return true;
  if (typeof value === 'boolean') return true;
  if (typeof value !== 'string') return false;
  const normalized = value.trim();
  if (normalized === '') return false;
  return (
    normalized !== 'N/A' &&
    normalized !== '0' &&
    normalized !== '{APPNAME}' &&
    normalized !== 'Unknown Publisher' &&
    !normalized.includes('%%')
  );
};

export const isHtml5Creative = (url) => {
  if (!url) return false;
  const lowerUrl = url.toLowerCase();
  return lowerUrl.includes('.html') || lowerUrl.includes('.zip');
};

export const isHtmlFileName = (str) => {
  if (!str) return false;
  const lower = str.toLowerCase();
  return lower.endsWith('.html') || lower.endsWith('.htm') || lower.endsWith('.zip');
};

export const isUnmaskedCreativeId = (name) => {
  if (!name) return false;
  return /^\d+$/.test(String(name));
};

export const formatCreativeName = (name) => {
  if (isUnmaskedCreativeId(name)) {
    return `Creative #${name}`;
  }
  return name || 'Unknown Creative';
};

export const getDomainColor = (domain) => {
  if (!domain) return '#636363';
  const palette = [
    '#f56a00',
    '#7265e6',
    '#ffbf00',
    '#00a2ae',
    '#722ed1',
    '#eb2f96',
    '#52c41a',
    '#13c2c2',
    '#fa541c',
    '#2f4554',
  ];
  let hash = 0;
  for (let i = 0; i < domain.length; i++) {
    hash = domain.charCodeAt(i) + ((hash << 5) - hash);
  }
  return palette[Math.abs(hash) % palette.length];
};

export const formatDuration = (seconds) => {
  if (!seconds || seconds < 5) return '';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) {
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  }
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`;
};

export const getPageTypeFromUrl = (url, events = []) => {
  if (!url) return { type: 'unknown', label: 'Unknown', color: '#8c8c8c' };

  let pathname = '';
  try {
    pathname = new URL(url).pathname.toLowerCase();
  } catch (e) {
    pathname = url.toLowerCase();
  }

  const eventTypes = new Set(events.map((e) => e.EVENT));

  // Transaction overrides everything
  if (eventTypes.has('transaction') || eventTypes.has('transaction_item')) {
    return { type: 'transaction', label: 'Purchase', color: '#52c41a' };
  }

  // Receipt / Confirmation
  if (
    pathname.includes('/receipt') ||
    pathname.includes('/confirmation') ||
    pathname.includes('/thank') ||
    pathname.includes('/success') ||
    pathname.includes('/order-confirm')
  ) {
    return { type: 'receipt', label: 'Receipt', color: '#52c41a' };
  }

  // Payment / Checkout
  if (pathname.includes('/payment') || pathname.includes('/checkout')) {
    if (eventTypes.has('submit_form')) {
      return { type: 'payment', label: 'Payment Complete', color: '#52c41a' };
    }
    return { type: 'payment', label: 'Payment', color: '#722ed1' };
  }

  // Cart
  if (pathname.includes('/cart') || pathname.includes('/basket') || pathname.includes('/bag')) {
    return { type: 'cart', label: 'Cart', color: '#fa8c16' };
  }

  // Login
  if (pathname.includes('/login') || pathname.includes('/signin') || pathname.includes('/account/login')) {
    return { type: 'login', label: 'Login', color: '#1890ff' };
  }

  // Signup / Create account
  if (
    pathname.includes('/signup') ||
    pathname.includes('/register') ||
    pathname.includes('/account/create')
  ) {
    if (eventTypes.has('submit_form')) {
      return { type: 'signup', label: 'Sign Up Complete', color: '#52c41a' };
    }
    return { type: 'signup', label: 'Sign Up', color: '#eb2f96' };
  }

  // Product / Content (default)
  return { type: 'page', label: 'Page', color: '#636363' };
};

export const groupJourneyEvents = (journey) => {
  if (!journey || !journey.length) return [];

  const groups = [];
  let currentGroup = null;

  const filtered = journey.filter((e) => e.EVENT !== 'record');

  filtered.forEach((event) => {
    const pageUrl = event.PAGE_URL || '';

    if (!currentGroup || currentGroup.pageUrl !== pageUrl) {
      if (currentGroup) groups.push(currentGroup);
      currentGroup = {
        pageUrl,
        startTime: event.DATE,
        endTime: event.DATE,
        events: [event],
      };
    } else {
      currentGroup.events.push(event);
      currentGroup.endTime = event.DATE;
    }
  });

  if (currentGroup) groups.push(currentGroup);

  // Enrich groups with metadata
  return groups.map((group) => {
    const start = new Date(group.startTime).getTime();
    const end = new Date(group.endTime).getTime();
    const durationSeconds = Math.round((end - start) / 1000);
    const eventTypes = new Set(group.events.map((e) => e.EVENT));
    const pageType = getPageTypeFromUrl(group.pageUrl, group.events);

    return {
      ...group,
      durationSeconds,
      pageType,
      hasPageView: eventTypes.has('page_view'),
      hasLinkClick: eventTypes.has('link_click'),
      hasSubmitForm: eventTypes.has('submit_form'),
      hasTransaction: eventTypes.has('transaction') || eventTypes.has('transaction_item'),
      pagePingCount: group.events.filter((e) => e.EVENT === 'page_ping').length,
      formEventCount: group.events.filter(
        (e) => e.EVENT === 'focus_form' || e.EVENT === 'change_form'
      ).length,
      eventCount: group.events.length,
    };
  });
};

export const getCleanUrl = (url) => {
  if (!url) return '';
  try {
    const u = new URL(url);
    return (u.hostname + u.pathname).replace(/^www\./, '');
  } catch (e) {
    const queryIndex = url.indexOf('?');
    return queryIndex === -1 ? url : url.slice(0, queryIndex);
  }
};
